import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import {
  TariffValidators,
  TariffItem,
  TariffStatus,
  TariffAction,
} from '../../types';
import { tariffsApi } from '../services';
import { TariffsRoutes } from '../../../../routes';

export interface TariffsListState {
  selectedList: string[];
  validators?: TariffValidators;
  pagination?: Pagination;
  tariffs?: TariffItem[];
  deleteIsSuccess?: boolean;
  errorsList?: string;
}

const initialState: TariffsListState = {
  selectedList: [],
};

export const tariffsListSlice = createSlice({
  name: 'tariffsList',
  initialState,
  reducers: {
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedList = action.payload;
    },
    setDeleteSuccess: (state) => {
      state.deleteIsSuccess = false;
    },
    clearTariffsListState: (state) => {
      state.tariffs = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      tariffsApi.endpoints.getTariffsValidator.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.getTariffsList.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.getTariffsList.matchFulfilled,
      (state, action: any) => {
        state.tariffs = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.getTariffsList.matchFulfilled,
      (state, action: PayloadAction<Response<TariffItem>>) => {
        state.tariffs = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(TariffsRoutes.TARIFFS_INFO, {
            tariffId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.createTariff.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        let nextTariffs: TariffItem[] = [];

        if (state.tariffs?.length) {
          nextTariffs = ([] as TariffItem[]).concat(state.tariffs);
        }

        nextTariffs.unshift({
          ...action.payload.data,
          url: generatePath(TariffsRoutes.TARIFFS_INFO, {
            tariffId: action.payload.data.id,
          }),
        });

        state.tariffs = nextTariffs;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.toggleTariffActive.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        const nextTariffs = state?.tariffs?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.isActive = action.payload.data.isActive;
          }

          return result;
        });

        state.tariffs = nextTariffs;
        state.selectedList = [];
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.duplicateTariff.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        if (state.tariffs) {
          const nextTariffs = ([] as TariffItem[]).concat(state.tariffs);

          nextTariffs.unshift({
            ...action.payload.data,
            url: generatePath(TariffsRoutes.TARIFFS_INFO, {
              tariffId: action.payload.data.id,
            }),
          });
          state.selectedList = [];
          state.tariffs = nextTariffs;
        }
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.updateTariff.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        if (state.tariffs) {
          const nextTariffs = state.tariffs.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                url: generatePath(TariffsRoutes.TARIFFS_INFO, {
                  tariffId: action.payload.data.id,
                }),
              };
            }

            return result;
          });

          state.tariffs = nextTariffs;
        }
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.changeTariffStatus.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: TariffItem }> & {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  status: TariffStatus;
                  previousTariffId?: string;
                  action?: TariffAction;
                  nextTariffId?: string;
                };
              };
            };
          };
        },
      ) => {
        if (state.tariffs) {
          const nextTariffs = state.tariffs.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                url: generatePath(TariffsRoutes.TARIFFS_INFO, {
                  tariffId: action.payload.data.id,
                }),
              };
            }

            if (
              result.id ===
                action.meta.arg.originalArgs.body.previousTariffId &&
              action.meta.arg.originalArgs.body.action ===
                TariffAction.REPLACE_ALL_AND_ARCHIVE_PREVIOUS
            ) {
              result.status = TariffStatus.ARCHIVED;
            }

            return result;
          });

          state.tariffs = nextTariffs;
        }
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.toggleTariffDefault.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        if (state.tariffs) {
          const nextTariffs = state.tariffs.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                url: generatePath(TariffsRoutes.TARIFFS_INFO, {
                  tariffId: action.payload.data.id,
                }),
              };
            }

            return result;
          });

          state.tariffs = nextTariffs;
        }
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.deleteTariff.matchFulfilled,
      (state) => {
        if (state.tariffs) {
          state.deleteIsSuccess = true;
        }
      },
    );
  },
});

export const { selectList, setDeleteSuccess, clearTariffsListState } =
  tariffsListSlice.actions;

export default tariffsListSlice.reducer;
